import { useCallback } from 'react'

import { useCreateEvent } from '@/shared/api/generated/analytics/analytics'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { MetaTrackEvent } from '@/shared/api/models/metaTrackEvent'
import { fbqTrack } from './meta-pixel'

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))

  return match?.[1]
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

export function useMetaTrack() {
  const { mutate } = useCreateEvent()
  const { profile } = usePixieWorldAuthContext()

  const trackMeta = useCallback(
    (event: MetaTrackEvent) => {
      const eventId = event.event_id || generateEventId()

      // Fire client-side pixel for deduplication. fbqTrack no-ops when the
      // pixel script isn't loaded (i.e. user rejected marketing cookies),
      // so this is safe to call unconditionally.
      const pixelParams: Record<string, unknown> = {}

      if (event.price) pixelParams.value = parseFloat(event.price)
      if (event.currency) pixelParams.currency = event.currency
      if (event.item_id) pixelParams.content_ids = event.item_id
      if (event.item_name) pixelParams.content_name = event.item_name
      if (event.item_category) pixelParams.content_category = event.item_category

      fbqTrack(event.event_name, pixelParams, eventId)

      // Send server-side CAPI event with same event_id. CAPI fires regardless
      // of cookie consent — server-to-server, no browser cookies required.
      // _fbp/_fbc are sent if present (improves match quality when the user
      // did consent) and omitted otherwise.
      mutate({
        data: {
          event_source_url: typeof window !== 'undefined' ? window.location.href : '',
          client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
          ...(profile ? {
            email: profile.email,
            external_id: profile.id?.toString(),
            first_name: profile.display_name || undefined,
          } : {}),
          ...event,
          event_id: eventId,
        },
      })
    },
    [mutate, profile],
  )

  return trackMeta
}
